import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { StoreProvider, useStore } from './store-hybrid';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DemoModeProvider } from './contexts/DemoModeContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Header } from './components/Header';
import { Login } from './components/Login';
import { FullPageLoader } from './components/LoadingSpinner';
import { DemoAutoLogin } from './components/DemoAutoLogin';
import { DemoBanner } from './components/DemoBanner';
import { Dashboard } from './pages/Dashboard';
import { BrandsPage } from './pages/Brands';
import { GaragePage } from './pages/Garage';
import { RentalPage } from './pages/Rental';
import { SalesPage } from './pages/Sales';
import { HistoryPage } from './pages/History';
import { FundPage } from './pages/Fund';
import { SuppliersPage } from './pages/Suppliers';

// App Content with Loading States
const AppContent: React.FC = () => {
  const { loading, error, isReconnecting, retryConnection } = useStore();

  if (loading) {
    return <FullPageLoader message="Loading your car rental data..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Database Connection Error
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <DemoBanner />
      <Header />
      
      {/* Database Status Indicator */}
      {error && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                <strong>⚠️ Database Status:</strong> {error}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                Your data is being stored locally and will sync when connection is restored.
              </p>
              {isReconnecting && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Attempting to reconnect...
                </p>
              )}
            </div>
            <div className="ml-4">
              <button
                onClick={retryConnection}
                disabled={isReconnecting}
                className="px-3 py-1 text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded hover:bg-amber-300 dark:hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isReconnecting ? 'Connecting...' : 'Retry Now'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <main>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/brands" element={<BrandsPage />} />
          <Route path="/garage" element={<GaragePage />} />
          <Route path="/rental" element={<RentalPage />} />
          <Route path="/sales" element={<SalesPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/fund" element={<FundPage />} />
          <Route path="/suppliers" element={<SuppliersPage />} />
        </Routes>
      </main>
    </div>
  );
};

// Protected Routes Component
const ProtectedApp: React.FC = () => {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return <Login />;
  }

  return (
    <StoreProvider>
      <DemoAutoLogin />
      <AppContent />
    </StoreProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <DemoModeProvider>
        <AuthProvider>
          <BrowserRouter>
            <ProtectedApp />
          </BrowserRouter>
        </AuthProvider>
      </DemoModeProvider>
    </ThemeProvider>
  );
}

export default App;
